import './personAvatar.css'

/**
 * A person's visual identity — their initials on a neutral chip.
 *
 * **There is no photo to render.** `MembershipResponse` carries `user_email`
 * and `user_display_name` and no image URL at all; `photo_url` exists only on
 * `UserResponse`, which is the SIGNED-IN user's own record. So a member's
 * avatar cannot be their picture, in this or any other component, until the
 * backend puts one on the membership schema.
 *
 * **And there is no hue.** D14 nominates a hue wheel "for identity", but
 * nothing implements one, and issues 290 and 299 are both open on exactly that
 * question — what palette an arbitrary identity draws from, and whether a
 * server-supplied colour can safely be a fill at all. M12 does not own that
 * decision, and a third colour-identity mechanism landing while two are
 * unsettled is what CLAUDE.md's rule about a third progress ring exists to
 * prevent. The fill is `--chip`, exactly as `MerchantAvatar` uses for a
 * merchant with no logo. Members stay distinguishable by name and role, both
 * of which are on the row.
 */
export function PersonAvatar({ name, email }: { name: string | null; email: string | null }) {
  return (
    <span className="person-avatar" aria-hidden="true">
      {initialsFor(name, email)}
    </span>
  )
}

/**
 * Two letters. From the display name when there is one: the first letter of
 * each of the first two WORDS for a multi-word name — `"Ana Maria Silva"` ->
 * `"AM"`, first two words rather than first-and-last. Nothing in
 * `MembershipResponse` splits a given name from a family name, so first-two-
 * words is the defensible reading; that is the convention pinned here, so the
 * next edit does not "fix" it toward first-and-last. A single-word name takes
 * its own first two letters instead.
 *
 * **The name-based branch above is new web behaviour with no iOS precedent.**
 * iOS's `HouseholdDetailView.swift:366` never derives initials from a name at
 * all — it is email-only. Only the fallback below mirrors iOS, and even there
 * loosely: Swift's `.prefix(2)` is grapheme-cluster-based, while this is
 * codepoint-based (see the note on `[...str]` below), so the two can disagree
 * on the same input.
 *
 * The email prefix when there is no name.
 *
 * `'?'` when there is neither. That is not defensive padding: both fields are
 * nullable on `MembershipResponse`, so a row with no name AND no email is a
 * shape the contract permits, and rendering an empty circle would look like a
 * layout bug rather than missing data.
 *
 * **`[...str]`, never `str[i]` or `str.slice(i, j)` directly.** `MerchantAvatar`
 * found this first: indexing or slicing by UTF-16 code unit can split a
 * surrogate pair in half, leaving a lone unpaired surrogate — a malformed
 * string, not merely the wrong letter — for any name with an emoji or another
 * astral-plane character next to another character. Spreading iterates by
 * Unicode code point instead, at the same cost, which is what every index and
 * slice below does. This component's job (two letters, plus an email
 * fallback) differs from `MerchantAvatar`'s (one letter, no email), so the two
 * are not unified into a shared helper — but the Unicode handling must not
 * diverge between them.
 */
export function initialsFor(name: string | null, email: string | null): string {
  const trimmed = name?.trim()
  if (trimmed) {
    const words = trimmed.split(/\s+/)
    const first = words[0] ?? ''
    const letters =
      words.length > 1
        ? ([...first][0] ?? '') + ([...(words[1] ?? '')][0] ?? '')
        : [...first].slice(0, 2).join('')
    return letters.toUpperCase()
  }
  const prefix = email?.split('@')[0]?.trim()
  if (prefix) return [...prefix].slice(0, 2).join('').toUpperCase()
  return '?'
}
